import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { message } from 'antd';
import { PAGINATION_PAGE_PARAM, PAGINATION_PER_PAGE_PARAM } from '@clockout-dev/design-elements';
import { calculateLedgerDateRange, resolveLedgerRenderZone } from '../../utils/dates';
import { buildLedgerParams, getLedgerStateFromUrl } from '../../utils/ledger';
import { LEDGER_LOAD_TOAST } from '../common/ledgerTable/ledgerTable.constants';
import {
    ADVANCE_TYPE,
    LEDGER_API_DATE_FORMAT,
    LEDGER_RECONCILIATION_FILTER,
    LEDGER_TYPE_LABELS,
} from '../../constants/ledger.constants';
import {
    DEFAULT_PAGE_SIZE,
    DEFAULT_TRANSACTION_FILTERS,
    TRANSACTION_DATE_FORMAT,
    TRANSACTION_FILTER_LABELS,
    TRANSACTION_FILTER_PARAM_KEYS,
    TRANSACTION_TIME_FRAMES,
    TRANSACTION_TIME_FRAME_LABELS,
    RECONCILIATION_FILTER_CHIP_OPTIONS,
    ADVANCE_TYPE_FILTER_CHIP_OPTIONS,
} from './reports.constants';

// Strict `dayjs(value, format, true)` needs the plugin; extending is idempotent.
dayjs.extend(customParseFormat);

const URL_DATE_FORMAT = LEDGER_API_DATE_FORMAT;

/**
 * The Transactions tab of /reports: fetching, filter state, URL sync and sort.
 * Export is server-side and shared with the Users tab
 * (`reports-export.controller.js`). Composed into `ReportsController` through `Inherit` (with
 * `ReportsTransactionsDelegate`), so `this` is the Reports page instance —
 * `this.state`, `this.setState`, `this.data` and the `SearchParamController`
 * helpers are all present at runtime. The shared table's URL reader and params
 * builder are plain functions in `utils/ledger`; nothing here inherits the table.
 *
 * Everything here is transaction-native. The user-attribute params the tab used
 * to send (enrollment / status / payroll / hasCompleteDDSwitch) are the Users
 * tab's and never leave this controller — see `buildTransactionsApiFilters`.
 *
 * Sorting and filtering are server-side: the table is paginated by the backend,
 * so the browser only ever holds one page. The URL is the single source of
 * truth for page / perPage / sort (written by the design system's Table, read
 * back through `getLedgerStateFromUrl`, exactly as the User page does) and for
 * the filters (written here), which is what makes a refresh or a shared link
 * land on the same slice.
 */
class ReportsTransactionsController {
    /**
     * Filters as they stand in the URL, validated value by value so a hand-edited
     * or stale link can never push an unknown value at the endpoint (which
     * answers 400 for anything off its allowlists).
     *
     * @returns {object} a complete filter object
     */
    resolveInitialTransactionFilters() {
        const K = TRANSACTION_FILTER_PARAM_KEYS;
        const next = { ...DEFAULT_TRANSACTION_FILTERS };

        // `type` shares the table's reader so the two cannot disagree on which
        // values are offered (fees included on this tab).
        const { type } = getLedgerStateFromUrl({ defaultPageSize: DEFAULT_PAGE_SIZE, showFees: true });
        next.type = type;

        const advanceType = this.getSearchParamValue(K.ADVANCE_TYPE);
        if (Object.values(ADVANCE_TYPE).includes(advanceType)) next.advanceType = advanceType;

        const reconciliation = this.getSearchParamValue(K.RECONCILIATION);
        if (Object.values(LEDGER_RECONCILIATION_FILTER).includes(reconciliation)) next.reconciliation = reconciliation;

        const timeFrame = this.getSearchParamValue(K.TIME_FRAME);
        if (TRANSACTION_TIME_FRAMES.includes(timeFrame)) next.timeFrame = timeFrame;

        const dateStart = this.getSearchParamValue(K.DATE_START);
        const dateEnd = this.getSearchParamValue(K.DATE_END);
        if (dateStart && dateEnd) {
            const start = dayjs(dateStart, URL_DATE_FORMAT, true);
            const end = dayjs(dateEnd, URL_DATE_FORMAT, true);
            // A half or inverted range is dropped rather than sent: the service
            // 400s both, and a 400 on first paint is a dead page.
            if (start.isValid() && end.isValid() && !end.isBefore(start, 'day')) {
                next.timeFrame = 'custom';
                next.dateRange = [start, end];
            }
        }
        if (next.timeFrame === 'custom' && !next.dateRange) next.timeFrame = DEFAULT_TRANSACTION_FILTERS.timeFrame;

        return next;
    }

    /**
     * Mirror the filter state into the URL. Defaults delete their key so the
     * URL stays clean; the range is written as YYYY-MM-DD because that is what
     * the API takes and what `resolveInitialTransactionFilters` parses.
     *
     * @param {object} filters
     */
    writeTransactionFiltersToUrl(filters) {
        const K = TRANSACTION_FILTER_PARAM_KEYS;
        const f = { ...DEFAULT_TRANSACTION_FILTERS, ...(filters || {}) };
        const custom = f.timeFrame === 'custom' && f.dateRange?.[0] && f.dateRange?.[1];

        this.setSearchParamValue(K.TYPE, f.type || null);
        this.setSearchParamValue(K.ADVANCE_TYPE, f.advanceType || null);
        this.setSearchParamValue(
            K.RECONCILIATION,
            f.reconciliation && f.reconciliation !== LEDGER_RECONCILIATION_FILTER.INCLUDE ? f.reconciliation : null,
        );
        this.setSearchParamValue(K.TIME_FRAME, f.timeFrame && f.timeFrame !== 'all' ? f.timeFrame : null);
        this.setSearchParamValue(K.DATE_START, custom ? f.dateRange[0].format(URL_DATE_FORMAT) : null);
        this.setSearchParamValue(K.DATE_END, custom ? f.dateRange[1].format(URL_DATE_FORMAT) : null);
    }

    /**
     * Drop every Transactions-tab key (filters, sort, search, page) from the URL
     * but leave the rest — notably `tab` — untouched. Used when the tab is
     * reset, and when the user leaves the tab so nothing carries across.
     */
    clearTransactionFiltersFromUrl() {
        Object.values(TRANSACTION_FILTER_PARAM_KEYS).forEach((key) => this.setSearchParamValue(key, null));
        this.setSearchParamValue(PAGINATION_PAGE_PARAM, null);
        this.setSearchParamValue(PAGINATION_PER_PAGE_PARAM, null);
    }

    /**
     * Whether anything narrows the result set. Drives the empty-state copy and
     * the chips row.
     *
     * @param {object} [filters] defaults to state
     * @param {string} [search] defaults to state
     * @returns {boolean}
     */
    hasActiveTransactionFilters(filters = this.state.transactionFilters, search = this.state.transactionsSearch) {
        return this.getActiveTransactionFilterChips(filters, search).length > 0;
    }

    /**
     * One chip per active filter, labelled from the constants.
     *
     * @param {object} [filters]
     * @param {string} [search]
     * @returns {Array<{key: string, label: string}>}
     */
    getActiveTransactionFilterChips(filters = this.state.transactionFilters, search = this.state.transactionsSearch) {
        const f = { ...DEFAULT_TRANSACTION_FILTERS, ...(filters || {}) };
        const chips = [];
        const L = TRANSACTION_FILTER_LABELS;

        if (f.type) {
            const labels = String(f.type)
                .split(',')
                .map((t) => LEDGER_TYPE_LABELS[t] || t);
            chips.push({ key: 'type', label: `${L.TYPE}: ${labels.join(', ')}` });
        }
        if (f.advanceType) {
            const option = ADVANCE_TYPE_FILTER_CHIP_OPTIONS.find((o) => o.value === f.advanceType);
            chips.push({ key: 'advanceType', label: `${L.ADVANCE_TYPE}: ${option?.label || f.advanceType}` });
        }
        if (f.reconciliation && f.reconciliation !== LEDGER_RECONCILIATION_FILTER.INCLUDE) {
            const option = RECONCILIATION_FILTER_CHIP_OPTIONS.find((o) => o.value === f.reconciliation);
            chips.push({ key: 'reconciliation', label: option?.label || f.reconciliation });
        }
        if (f.timeFrame && f.timeFrame !== 'all') {
            const custom = f.timeFrame === 'custom' && f.dateRange?.[0] && f.dateRange?.[1];
            const range = custom
                ? `${f.dateRange[0].format(TRANSACTION_DATE_FORMAT)} – ${f.dateRange[1].format(TRANSACTION_DATE_FORMAT)}`
                : TRANSACTION_TIME_FRAME_LABELS[f.timeFrame] || f.timeFrame;
            if (custom || f.timeFrame !== 'custom')
                chips.push({ key: 'createdDate', label: `${L.CREATED_DATE}: ${range}` });
        }
        const term = (search || '').trim();
        if (term) chips.push({ key: 'search', label: `${L.SEARCH}: ${term}` });

        return chips;
    }

    /**
     * Query params for `fetchLedger` on this tab — transaction attributes only,
     * on top of the shared `buildLedgerParams` (page, size, sort, type). Never
     * `enrollment`, `status`, `payroll` or `hasCompleteDDSwitch`: those are user
     * attributes the Users tab owns, and sending them here was the "filters
     * carry over silently" bug.
     *
     * The preset ranges ("Last 7 days", "Last 30 days") are computed in the zone
     * the endpoint evaluates `start` / `end` in — the one it declared on the last
     * payload (`transactionsTimezone`), else the build-time default — not in the
     * viewer's. Computed locally, a preset picked at 22:00 Pacific would send
     * tomorrow's date as `end`.
     *
     * @param {object} filters
     * @param {{page?: number, perPage?: number, sort?: object|null, search?: string, zone?: string|null}} [options]
     *   `zone` defaults to the zone in state
     * @returns {object}
     */
    buildTransactionsApiFilters(filters, { page, perPage, sort = null, search = '', zone } = {}) {
        const f = { ...DEFAULT_TRANSACTION_FILTERS, ...(filters || {}) };
        const api = buildLedgerParams({ page, perPage, sort, type: f.type });

        const rangeZone = resolveLedgerRenderZone(zone ?? this.state?.transactionsTimezone);
        const { start, end } = calculateLedgerDateRange(f.timeFrame, f.dateRange, rangeZone);
        // Both or neither — the service 400s a half range, and the range helper
        // returns nulls for a custom frame with no selection yet.
        if (start && end) {
            api.start = start;
            api.end = end;
        }
        if (f.advanceType) api.advanceType = f.advanceType;
        if (f.reconciliation && f.reconciliation !== LEDGER_RECONCILIATION_FILTER.INCLUDE) {
            api.reconciliation = f.reconciliation;
        }
        const term = (search || '').trim();
        if (term) api.search = term;

        return api;
    }

    /**
     * Normalizes the ledger API payload (`items` paginated or legacy
     * `transactions`) into a flat transaction row array.
     *
     * @param {Object} [ledger]
     * @returns {Array}
     */
    extractLedgerTransactions(ledger) {
        return ledger?.items || ledger?.transactions || [];
    }

    /**
     * Load one page of transactions with the given (or current) filters, sort
     * and search. Records the timezone the endpoint declared so the table
     * renders and labels in that zone.
     *
     * Every response is checked against `transactionsRequestId` before it is
     * applied. Page, sort, filters, search and Refresh all enter through this
     * one path, and the table can be re-sorted before the previous fetch
     * answers — without the token a slow first response lands after a fast
     * second one and the rows on screen no longer match the headers, the chips
     * or the URL claiming to describe them. Same guard as the User page's
     * `loadTransactions`.
     *
     * `fetchLedger` rethrows. On failure the rows are cleared and
     * `transactionsFailed` is set: the rows on screen belong to the previous
     * slice, so leaving them under the new chips would show a set the server
     * never returned, and an empty table with no message would read as "no
     * transactions match".
     *
     * @param {number} [page]
     * @param {number} [perPage]
     * @param {{filters?: object, sort?: object|null, search?: string}} [options] default to state
     */
    async loadTransactions(page, perPage, options = {}) {
        const filters = options.filters ?? this.state.transactionFilters ?? DEFAULT_TRANSACTION_FILTERS;
        const sort = options.sort !== undefined ? options.sort : (this.state.transactionsSort ?? null);
        const search = options.search !== undefined ? options.search : (this.state.transactionsSearch ?? '');
        const resolvedPage = page ?? this.state.transactionsPageInfo?.current ?? 1;
        const resolvedPerPage = perPage ?? this.state.transactionsPageInfo?.perPage ?? DEFAULT_PAGE_SIZE;

        const requestId = (this.transactionsRequestId || 0) + 1;
        this.transactionsRequestId = requestId;
        const isCurrent = () => this.transactionsRequestId === requestId;

        this.setState({ transactionsLoading: true, transactionsFailed: false });
        try {
            const ledger = await this.data.fetchLedger(
                this.buildTransactionsApiFilters(filters, {
                    page: resolvedPage,
                    perPage: resolvedPerPage,
                    sort,
                    search,
                }),
            );
            if (!isCurrent()) return;
            const next = this.extractLedgerTransactions(ledger);
            const total = ledger?.pageinfo?.count ?? next.length;
            this.setState({
                transactionsRows: next,
                transactionsPageInfo: {
                    current: resolvedPage,
                    // The endpoint clamps pageSize and echoes the effective value.
                    perPage: ledger?.pageinfo?.perPage ?? resolvedPerPage,
                    total,
                },
                transactionsTimezone: resolveLedgerRenderZone(ledger?.timezone),
                transactionFilters: filters,
                transactionsSort: sort,
            });
        } catch (error) {
            console.error('Error loading transactions:', error);
            if (!isCurrent()) return;
            this.setState({
                transactionsRows: [],
                transactionsFailed: true,
                transactionsPageInfo: { current: resolvedPage, perPage: resolvedPerPage, total: 0 },
                transactionFilters: filters,
                transactionsSort: sort,
            });
            message.error(LEDGER_LOAD_TOAST.FAILED);
        } finally {
            if (isCurrent()) this.setState({ transactionsLoading: false });
        }
    }

    /**
     * antd `onChange` for the transactions table.
     *
     * By the time this runs the design system's Table has written the new page,
     * sort and column filters to the query string (`saveSearchParams`),
     * including the reset to page 1 whenever a sort re-slices the set. So this
     * reads that state back — the same way the User page does — instead of
     * re-deriving it from antd's arguments: one writer, one reader, and no way
     * for the URL and the fetch to disagree.
     */
    handleTransactionsTableChange() {
        const { page, perPage, sort, type } = getLedgerStateFromUrl({
            defaultPageSize: DEFAULT_PAGE_SIZE,
            showFees: true,
        });
        const filters = { ...this.state.transactionFilters, type };
        this.setState({ transactionsSort: sort, transactionFilters: filters });
        this.loadTransactions(page, perPage, { filters, sort });
    }

    /**
     * Apply the (staged or given) filters: mirror them into the URL, go back to
     * page 1 and re-fetch. The sort is kept — a filter narrows the set, it does
     * not change how it is ordered.
     *
     * @param {object} [filtersOverride] use when state may not have flushed yet
     */
    async applyTransactionFilters(filtersOverride) {
        const filters = filtersOverride ?? this.state.transactionFilters;
        const perPage = this.state.transactionsPageInfo?.perPage ?? DEFAULT_PAGE_SIZE;

        this.writeTransactionFiltersToUrl(filters);
        this.setSearchParamValue(PAGINATION_PAGE_PARAM, null);
        this.setState({ transactionFilters: filters });
        await this.loadTransactions(1, perPage, { filters });
    }

    /**
     * One action clears everything on this tab: filters, search and sort, in
     * state and in the URL. Reloads page 1 with the server's chronological
     * default.
     *
     * `transactionsSearchResetKey` is bumped because the search box is
     * uncontrolled (`DebouncedSearch` reads `?search=` once, on mount): clearing
     * the state and the URL empties the fetch and the chip but leaves the typed
     * term in the input. The bar keys the box on this counter, so the reset
     * remounts it empty and the screen agrees with the fetch.
     */
    async resetTransactionFilters() {
        const perPage = this.state.transactionsPageInfo?.perPage ?? DEFAULT_PAGE_SIZE;
        this.clearTransactionFiltersFromUrl();
        this.setState({
            transactionFilters: { ...DEFAULT_TRANSACTION_FILTERS },
            transactionsSearch: '',
            transactionsSort: null,
            transactionsPageInfo: { current: 1, perPage, total: 0 },
            transactionsSearchResetKey: (this.state.transactionsSearchResetKey || 0) + 1,
        });
        await this.loadTransactions(1, perPage, {
            filters: { ...DEFAULT_TRANSACTION_FILTERS },
            sort: null,
            search: '',
        });
    }

    /**
     * Fired by `DebouncedSearch` on this tab (it writes `?search=` itself).
     * Resets to page 1 and re-fetches with the current filters + new term.
     * @param {string} term
     */
    handleTransactionsSearch(term) {
        const search = term || '';
        this.setState({ transactionsSearch: search }, () => this.applyTransactionFilters());
    }
}

export default ReportsTransactionsController;
